import { apiClient, ApiResponse } from './api-client'
import {
  Case,
  CaseFilters,
  PaginationOptions,
  CaseListResponse,
  CreateCaseDto,
  UpdateCaseDto,
  ChangeCaseStatusDto,
  CaseStats,
  CaseParty,
  CaseDocument,
  CaseTimelineEvent,
  CaseStatus,
  CasePriority
} from '../types/case'

class CaseService {
  private baseUrl = '/api/cases'

  async getAllCases(filters: CaseFilters = {}, pagination: PaginationOptions = { page: 1, limit: 20, sort: 'createdAt:desc' }): Promise<CaseListResponse> {
    const params = new URLSearchParams()
    
    // Add pagination
    params.append('page', pagination.page.toString())
    params.append('limit', pagination.limit.toString())
    if (pagination.sort) params.append('sort', pagination.sort)
    
    // Add filters
    if (filters.search) params.append('search', filters.search)
    if (filters.clientId) params.append('clientId', filters.clientId)
    if (filters.status && filters.status.length > 0) {
      filters.status.forEach(status => params.append('status', status))
    }
    if (filters.area && filters.area.length > 0) {
      filters.area.forEach(area => params.append('area', area))
    }
    if (filters.priority && filters.priority.length > 0) {
      filters.priority.forEach(priority => params.append('priority', priority))
    }
    if (filters.responsibleLawyerId) params.append('responsibleLawyerId', filters.responsibleLawyerId)
    if (filters.startDateFrom) params.append('startDateFrom', filters.startDateFrom)
    if (filters.startDateTo) params.append('startDateTo', filters.startDateTo)
    if (filters.expectedEndDateFrom) params.append('expectedEndDateFrom', filters.expectedEndDateFrom)
    if (filters.expectedEndDateTo) params.append('expectedEndDateTo', filters.expectedEndDateTo)
    if (filters.estimatedValueMin !== undefined) params.append('estimatedValueMin', filters.estimatedValueMin.toString())
    if (filters.estimatedValueMax !== undefined) params.append('estimatedValueMax', filters.estimatedValueMax.toString())
    if (filters.tags && filters.tags.length > 0) {
      filters.tags.forEach(tag => params.append('tags', tag))
    }
    if (filters.hasCourtInfo !== undefined) params.append('hasCourtInfo', filters.hasCourtInfo.toString())
    if (filters.hasEstimatedValue !== undefined) params.append('hasEstimatedValue', filters.hasEstimatedValue.toString())
    if (filters.hasResponsibleLawyer !== undefined) params.append('hasResponsibleLawyer', filters.hasResponsibleLawyer.toString())
    if (filters.onlyLegalProcesses !== undefined) params.append('onlyLegalProcesses', filters.onlyLegalProcesses.toString())
    if (filters.excludeLegalProcesses !== undefined) params.append('excludeLegalProcesses', filters.excludeLegalProcesses.toString())

    const response = await apiClient.get<ApiResponse<CaseListResponse>>(`${this.baseUrl}?${params}`)
    return response.data.data
  }

  async getCaseById(id: string): Promise<Case> {
    const response = await apiClient.get<ApiResponse<{ case: Case }>>(`${this.baseUrl}/${id}`)
    return response.data.data.case
  }

  async createCase(caseData: CreateCaseDto): Promise<Case> {
    const response = await apiClient.post<ApiResponse<{ case: Case }>>(this.baseUrl, caseData)
    return response.data.data.case
  }

  async updateCase(id: string, caseData: UpdateCaseDto): Promise<Case> {
    const response = await apiClient.put<ApiResponse<{ case: Case }>>(`${this.baseUrl}/${id}`, caseData)
    return response.data.data.case
  }

  async deleteCase(id: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`)
  }

  async changeCaseStatus(id: string, statusData: ChangeCaseStatusDto): Promise<Case> {
    const response = await apiClient.patch<ApiResponse<{ case: Case }>>(`${this.baseUrl}/${id}/status`, statusData)
    return response.data.data.case
  }

  async getCaseStats(): Promise<CaseStats> {
    const response = await apiClient.get<ApiResponse<{ stats: CaseStats }>>(`${this.baseUrl}/stats`)
    return response.data.data.stats
  }

  async searchCases(query: string, filters: Partial<CaseFilters> = {}): Promise<Case[]> {
    const params = new URLSearchParams()
    params.append('q', query)
    
    // Add additional filters
    if (filters.status && filters.status.length > 0) {
      filters.status.forEach(status => params.append('status', status))
    }
    if (filters.area && filters.area.length > 0) {
      filters.area.forEach(area => params.append('area', area))
    }
    if (filters.priority && filters.priority.length > 0) {
      filters.priority.forEach(priority => params.append('priority', priority))
    }
    if (filters.clientId) params.append('clientId', filters.clientId)
    if (filters.responsibleLawyerId) params.append('responsibleLawyerId', filters.responsibleLawyerId)

    const response = await apiClient.get<ApiResponse<{ cases: Case[] }>>(`${this.baseUrl}/search?${params}`)
    return response.data.data.cases
  }

  // Case parties management
  async getCaseParties(caseId: string): Promise<CaseParty[]> {
    const response = await apiClient.get<ApiResponse<{ parties: CaseParty[] }>>(`${this.baseUrl}/${caseId}/parties`)
    return response.data.data.parties
  }

  async addCaseParty(caseId: string, partyData: Omit<CaseParty, 'id' | 'caseId' | 'createdAt' | 'updatedAt'>): Promise<CaseParty> {
    const response = await apiClient.post<ApiResponse<{ party: CaseParty }>>(`${this.baseUrl}/${caseId}/parties`, partyData)
    return response.data.data.party
  }

  async updateCaseParty(caseId: string, partyId: string, partyData: Partial<Omit<CaseParty, 'id' | 'caseId' | 'createdAt' | 'updatedAt'>>): Promise<CaseParty> {
    const response = await apiClient.put<ApiResponse<{ party: CaseParty }>>(`${this.baseUrl}/${caseId}/parties/${partyId}`, partyData)
    return response.data.data.party
  }

  async deleteCaseParty(caseId: string, partyId: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.baseUrl}/${caseId}/parties/${partyId}`)
  }

  // Case documents management
  async getCaseDocuments(caseId: string): Promise<CaseDocument[]> {
    const response = await apiClient.get<ApiResponse<{ documents: CaseDocument[] }>>(`${this.baseUrl}/${caseId}/documents`)
    return response.data.data.documents
  }

  /** Troca a descrição (finalidade) do documento; null apaga. */
  async updateDocumentDescription(caseId: string, documentId: string, description: string | null): Promise<CaseDocument> {
    const response = await apiClient.put<ApiResponse<{ document: CaseDocument }>>(`${this.baseUrl}/${caseId}/documents/${documentId}`, { descricao: description })
    return response.data.data.document
  }

  async deleteCaseDocument(caseId: string, documentId: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.baseUrl}/${caseId}/documents/${documentId}`)
  }

  // Case timeline management
  async getCaseTimeline(caseId: string): Promise<CaseTimelineEvent[]> {
    const response = await apiClient.get<ApiResponse<{ events: CaseTimelineEvent[] }>>(`${this.baseUrl}/${caseId}/timeline`)
    return response.data.data.events
  }

  async addTimelineEvent(caseId: string, eventData: Omit<CaseTimelineEvent, 'id' | 'caseId' | 'createdBy' | 'createdAt' | 'updatedAt'>): Promise<CaseTimelineEvent> {
    const response = await apiClient.post<ApiResponse<{ event: CaseTimelineEvent }>>(`${this.baseUrl}/${caseId}/timeline`, eventData)
    return response.data.data.event
  }

  async updateTimelineEvent(caseId: string, eventId: string, eventData: Partial<Omit<CaseTimelineEvent, 'id' | 'caseId' | 'createdBy' | 'createdAt' | 'updatedAt'>>): Promise<CaseTimelineEvent> {
    const response = await apiClient.put<ApiResponse<{ event: CaseTimelineEvent }>>(`${this.baseUrl}/${caseId}/timeline/${eventId}`, eventData)
    return response.data.data.event
  }

  async deleteTimelineEvent(caseId: string, eventId: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.baseUrl}/${caseId}/timeline/${eventId}`)
  }

  // Helper method to format case display data
  formatCaseDisplayData(caseData: Case): {
    statusColor: string
    priorityColor: string
    estimatedValueFormatted: string
    daysUntilDeadline?: number
    isOverdue?: boolean
  } {
    const statusColors: Partial<Record<CaseStatus, string>> = {
      ACTIVE: 'text-primary bg-primary/10',
      SUSPENDED: 'text-foreground bg-muted',
      CLOSED_WON: 'text-primary bg-primary/5',
      CLOSED_LOST: 'text-destructive bg-destructive/10',
      CLOSED_SETTLED: 'text-secondary-foreground bg-secondary',
      ARCHIVED: 'text-muted-foreground bg-muted'
    }

    const priorityColors: Partial<Record<CasePriority, string>> = {
      LOW: 'text-muted-foreground',
      MEDIUM: 'text-foreground',
      HIGH: 'text-destructive',
      URGENT: 'font-semibold text-destructive'
    }

    let daysUntilDeadline: number | undefined
    let isOverdue: boolean | undefined

    if (caseData.expectedEndDate) {
      const deadline = new Date(caseData.expectedEndDate)
      const today = new Date()
      const timeDiff = deadline.getTime() - today.getTime()
      daysUntilDeadline = Math.ceil(timeDiff / (1000 * 3600 * 24))
      isOverdue = daysUntilDeadline < 0
    }

    return {
      statusColor: statusColors[caseData.status] ?? statusColors.ACTIVE ?? '',
      priorityColor: priorityColors[caseData.priority] ?? priorityColors.MEDIUM ?? '',
      estimatedValueFormatted: caseData.estimatedValue
        ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(caseData.estimatedValue)
        : 'Não informado',
      daysUntilDeadline,
      isOverdue
    }
  }

  // Document methods
  async uploadDocument(
    caseId: string,
    file: File,
    documentType: string,
    isConfidential: boolean = false,
    description?: string
  ): Promise<CaseDocument> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('documentType', documentType)
    formData.append('isConfidential', String(isConfidential))
    if (description?.trim()) formData.append('descricao', description.trim())

    const response = await apiClient.post<ApiResponse<{ document: CaseDocument }>>(
      `${this.baseUrl}/${caseId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data.data.document
  }

  async downloadDocument(caseId: string, documentId: string): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${caseId}/documents/${documentId}/download`, {
      responseType: 'blob'
    })
    return response.data
  }

  async previewDocument(caseId: string, documentId: string): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${caseId}/documents/${documentId}/preview`, {
      responseType: 'blob'
    })
    return response.data
  }

  async deleteDocument(caseId: string, documentId: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${caseId}/documents/${documentId}`)
  }

  // Conversion method
  async convertToLegalProcess(
    caseId: string,
    data: {
      courtNumber: string;
      courtName?: string;
      judgeName?: string;
    }
  ): Promise<Case> {
    const response = await apiClient.post<ApiResponse<{ case: Case }>>(
      `${this.baseUrl}/${caseId}/convert-to-legal-process`,
      data
    );
    return response.data.data.case;
  }

  /**
   * Search cases by process number
   */
  async findByProcessNumber(
    processNumber: string
  ): Promise<{
    found: boolean;
    cases: Array<{
      id: string;
      caseNumber: string;
      internalNumber: string;
      title: string;
      clientName: string;
      status: string;
    }>;
  }> {
    const response = await apiClient.get<ApiResponse<{
      found: boolean;
      cases: Array<{
        id: string;
        caseNumber: string;
        internalNumber: string;
        title: string;
        clientName: string;
        status: string;
      }>;
    }>>(
      `${this.baseUrl}/by-process-number/${encodeURIComponent(processNumber)}`
    );
    return response.data.data;
  }

  /**
   * Create case from publication
   */
  async createFromPublication(
    data: CreateCaseDto & {
      sourcePublicationId?: number;
      publicationData?: {
        numeroProcesso: string;
        tribunal: string;
        orgao: string;
      };
    }
  ): Promise<Case> {
    const response = await apiClient.post<ApiResponse<{ case: Case }>>(
      `${this.baseUrl}/from-publication`,
      data
    );
    return response.data.data.case;
  }
}

export const caseService = new CaseService()